import { DateTime } from 'luxon'
import React, { useEffect, useState } from 'react'

import activeCurrentInner from 'assets/gameStreak/active-current-inner.svg'
import activeCurrentOuter from 'assets/gameStreak/active-current-outer.svg'
import clock from 'assets/gameStreak/clock.svg'
import completed from 'assets/gameStreak/completed.png'
import expiringCurrentInner from 'assets/gameStreak/expiring-current-inner.svg'
import expiringCurrentOuter from 'assets/gameStreak/expiring-current-outer.svg'
import inactiveInner from 'assets/gameStreak/inactive-inner.svg'
import inactiveOuter from 'assets/gameStreak/inactive-outer.svg'
import lostCurrentInner from 'assets/gameStreak/lost-current-inner.svg'
import lostCurrentOuter from 'assets/gameStreak/lost-current-outer.svg'
import RewardPoints from 'components/RewardPoints'
import {
  getActiveStreakProgress,
  getLatestStreakProgress,
  getVisibleStreakDays,
} from 'utils/streak'

import './index.css'

const STATUS_COPY = {
  incomplete: {
    timerLabel: 'Limited time:',
    title: (currentDay, totalDays) => `Complete Day ${currentDay} of ${totalDays}!`,
  },
  completed: {
    timerLabel: 'Start time in:',
    title: (currentDay, totalDays) => `Day ${currentDay} of ${totalDays} Completed!`,
  },
  expiring: {
    timerLabel: 'Limited time:',
    title: (currentDay, totalDays) => `Save Day ${currentDay} of ${totalDays}!`,
  },
  lost: {
    title: (currentDay, totalDays) => `Day ${currentDay} of ${totalDays} Missed!`,
  },
  finished: {
    title: (_, totalDays) => `Day ${totalDays} Completed!`,
  },
}

const getSecondsLeft = (now, timezone) => {
  const localNow = now.setZone(timezone)
  return Math.max(
    0,
    Math.floor(localNow.plus({ days: 1 }).startOf('day').diff(localNow, 'seconds').seconds)
  )
}

const formatTimeLeft = (seconds) =>
  [Math.floor(seconds / 3600), Math.floor((seconds % 3600) / 60), seconds % 60]
    .map((value) => String(value).padStart(2, '0'))
    .join(':')

const MILESTONE_IMAGES = {
  active: [activeCurrentOuter, activeCurrentInner],
  expiring: [expiringCurrentOuter, expiringCurrentInner],
  inactive: [inactiveOuter, inactiveInner],
  lost: [lostCurrentOuter, lostCurrentInner],
}

const StreakMilestone = ({ isHighlighted, type, value }) => (
  <div
    className={`gameStreakMilestone ${type}${isHighlighted ? ' highlighted' : ''}`}
    role="img"
    aria-label={`Day ${value}: ${type}${isHighlighted ? ', highlighted' : ''}`}
  >
    {type === 'completed' ? (
      <img src={completed} alt="" />
    ) : (
      <>
        <img src={MILESTONE_IMAGES[type][0]} alt="" />
        <img className="gameStreakMilestoneInner" src={MILESTONE_IMAGES[type][1]} alt="" />
      </>
    )}
    <span>{value}</span>
  </div>
)

const GameStreakCard = ({ tasks, isLocked = false }) => {
  const sortedTasks = [...tasks].sort(
    (first, second) => first.streakRule.test.count - second.streakRule.test.count
  )
  const task = sortedTasks[sortedTasks.length - 1]
  const nextRewardTask = sortedTasks.find((streakTask) => !streakTask.completed) || task
  const timezone = task?.streak?.userTimezone || 'America/Chicago'
  const [now, setNow] = useState(DateTime.now)
  const today = now.setZone(timezone).toISODate()
  const yesterday = now.setZone(timezone).minus({ days: 1 }).toISODate()
  const events = task?.streak?.events || []
  const latestEvent = [...events].sort().pop()
  const completedToday = events.includes(today)
  const secondsLeft = getSecondsLeft(now, timezone)
  const isLost = latestEvent && latestEvent < yesterday
  const isExpiring = latestEvent === yesterday && secondsLeft <= 30 * 60
  const currentStatus = task.completed
    ? 'finished'
    : completedToday
    ? 'completed'
    : isLost
    ? 'lost'
    : isExpiring
    ? 'expiring'
    : 'incomplete'
  const totalDays = task.streakRule.test.count
  const progress = isLost
    ? getLatestStreakProgress(events)
    : getActiveStreakProgress(events, timezone, now)
  const currentDay =
    currentStatus === 'finished'
      ? totalDays
      : Math.min(progress + (completedToday ? 0 : 1), totalDays)
  const reward = nextRewardTask.rewards?.piggyBanks || 0
  const totalPiggyBanks = sortedTasks.reduce(
    (sum, streakTask) => sum + (streakTask.rewards?.piggyBanks || 0),
    0
  )
  const rewardDay = nextRewardTask.streakRule.test.count
  const copy = STATUS_COPY[currentStatus]
  const timerValue = formatTimeLeft(secondsLeft)
  const milestones = sortedTasks.map((streakTask) => streakTask.streakRule.test.count)
  const milestoneSet = new Set(milestones)
  const completedMilestones = new Set(
    sortedTasks
      .filter((streakTask) => streakTask.completed)
      .map((streakTask) => streakTask.streakRule.test.count)
  )
  const visibleDays = getVisibleStreakDays(currentDay, milestones)
  useEffect(() => {
    if (!task || task.completed || isLocked) return
    const interval = setInterval(() => setNow(DateTime.now()), 1000)
    return () => clearInterval(interval)
  }, [task, task?.completed, isLocked])

  const isDayCompleted = (day) =>
    currentStatus === 'finished' ||
    (milestoneSet.has(day) ? completedMilestones.has(day) : day <= progress)
  const highlightedDay = isLocked ? undefined : currentDay

  const getFlameType = (day) => {
    if (isLocked) return 'inactive'
    if (isDayCompleted(day)) return 'completed'
    if (day === currentDay && currentStatus === 'expiring') return 'expiring'
    if (day === currentDay && currentStatus === 'lost') return 'lost'
    if (day === currentDay) return 'active'
    return 'inactive'
  }

  return (
    <section className={`gameStreak${isLocked ? ' locked' : ''}`}>
      <div className={`gameStreakPanel ${currentStatus}`}>
        {copy.timerLabel && !isLocked && (
          <div className="gameStreakTimer">
            <img src={clock} alt="" />
            <span>
              <span>{copy.timerLabel}</span>
              <strong>{timerValue}</strong>
            </span>
          </div>
        )}

        <header className="gameStreakHeader">
          <h3>
            {copy.title(currentDay, totalDays)}
            {totalPiggyBanks > 0 && (
              <span className="gameStreakHeaderTotal">
                {' '}
                (up to {totalPiggyBanks} <img src="/images/pig.png" alt="" />)
              </span>
            )}
          </h3>
          {isLocked && <img className="gameStreakLock" src="/images/lock.png" alt="Locked" />}
        </header>

        <div className="gameStreakTrack" aria-label={`Day ${currentDay} of ${totalDays}`}>
          {visibleDays.map((day, index) => (
            <React.Fragment key={day}>
              {index > 0 && (
                <div
                  className={`gameStreakLine ${
                    isDayCompleted(visibleDays[index - 1]) && isDayCompleted(day) ? 'completed' : ''
                  }`}
                />
              )}
              <StreakMilestone
                isHighlighted={day === highlightedDay}
                type={getFlameType(day)}
                value={day}
              />
            </React.Fragment>
          ))}
        </div>

        <div className="gameStreakTask">
          <span>
            {currentStatus === 'finished' ? 'Reward Unlocked!' : `Play ${rewardDay} days to earn!`}
          </span>
          <RewardPoints
            value={reward}
            isPig={true}
            isLocked={isLocked}
            showBG={true}
            avoidResponsiveness={true}
          />
        </div>
      </div>
    </section>
  )
}

export default GameStreakCard
